import {URL_QUERY_KEYS} from '@common/constants/network';
import type {SortDirection} from './types';

export interface UrlStateKeys {
  page: string;
  pageSize: string;
  search: string;
  sort: string;
}

/** Build URL param keys, optionally prefixed for multiple tables on one page. */
export function buildUrlKeys(prefix?: string): UrlStateKeys {
  const p = prefix ? `${prefix}.` : '';
  return {
    page: `${p}${URL_QUERY_KEYS.PAGE}`,
    pageSize: `${p}${URL_QUERY_KEYS.PAGE_SIZE}`,
    search: `${p}${URL_QUERY_KEYS.SEARCH}`,
    sort: `${p}${URL_QUERY_KEYS.SORT}`,
  };
}

export function filterKey(name: string, prefix?: string): string {
  return prefix ? `${prefix}.${name}` : name;
}

/** Encode `{key: 'createdAt', direction: 'desc'}` → `"createdAt:desc"`. */
export function encodeSort(
  sort: {key: string; direction: SortDirection} | null,
): string | null {
  if (!sort) return null;
  return `${sort.key}:${sort.direction}`;
}

/** Decode `"createdAt:desc"` → `{key: 'createdAt', direction: 'desc'}`. Tolerates colons in the key. */
export function decodeSort(
  raw: string | null,
): {key: string; direction: SortDirection} | null {
  if (!raw) return null;
  const idx = raw.lastIndexOf(':');
  if (idx === -1) return null;
  const direction = raw.slice(idx + 1);
  if (direction !== 'asc' && direction !== 'desc') return null;
  const key = raw.slice(0, idx);
  if (!key) return null;
  return {key, direction};
}

/** Sort toggle cycle: off → asc → desc → off. */
export function nextSortDirection(
  current: {key: string; direction: SortDirection} | null,
  clickedKey: string,
): {key: string; direction: SortDirection} | null {
  if (!current || current.key !== clickedKey) {
    return {key: clickedKey, direction: 'asc'};
  }
  if (current.direction === 'asc') {
    return {key: clickedKey, direction: 'desc'};
  }
  return null;
}

/** Coerce a URL string back to a typed filter value based on the default's runtime type. */
export function coerceFilterValue<T>(raw: string, defaultValue: T): T {
  if (typeof defaultValue === 'boolean') {
    return (raw === 'true') as unknown as T;
  }
  if (typeof defaultValue === 'number') {
    const n = Number(raw);
    return (Number.isNaN(n) ? defaultValue : n) as unknown as T;
  }
  return raw as unknown as T;
}
